/**
 * Regras puras das máscaras de número (CampoValor / CampoPercentual).
 *
 * Por que existe (QA 26/09): o NumericFormat, sem `allowedDecimalSeparators`,
 * trata o "." como vírgula decimal. Quem digitava "1.500,00" (do jeito
 * brasileiro) gravava R$ 1,50 — mil vezes menor, sem aviso. Colar "1,500.00"
 * de uma invoice em dólar tinha o mesmo problema.
 *
 * Tudo aqui é função pura (sem React) para ser testado isolado.
 */

// Símbolo exibido como prefixo. Aceita o id interno do orçamento
// (real/dolar/euro/iene/libra/dolarcad — ver `obterMoedaOrcamento`), o código ISO
// (BRL/USD/EUR/JPY) ou o próprio símbolo.
const SIMBOLOS_MOEDA = {
    real: 'R$', brl: 'R$', 'r$': 'R$',
    dolar: 'US$', 'dólar': 'US$', usd: 'US$', 'us$': 'US$', $: 'US$',
    euro: '€', eur: '€', '€': '€',
    iene: '¥', jpy: '¥', '¥': '¥',
    libra: '£', gbp: '£', '£': '£',
    dolarcad: 'C$', cad: 'C$', 'c$': 'C$',
};

export const simboloMoeda = (moeda) => {
    if (moeda === null || moeda === undefined || moeda === false || moeda === '') return '';
    const chave = String(moeda).trim().toLowerCase();
    return SIMBOLOS_MOEDA[chave] || String(moeda).trim();
};

// "1.500" / "1.500.000" só são milhar quando o primeiro grupo tem 1 a 3 dígitos
// (sem zero à esquerda — "0.500" é meio, não quinhentos) e TODOS os grupos
// seguintes têm exatamente 3 dígitos.
const ehAgrupamentoMilhar = (partes) => {
    if (partes.length < 2) return false;
    if (!/^[1-9]\d{0,2}$/.test(partes[0])) return false;
    return partes.slice(1).every((grupo) => /^\d{3}$/.test(grupo));
};

/**
 * Converte um texto colado (ou digitado solto) em string numérica canônica,
 * com "." como separador decimal e sem milhar: "1.500,00" → "1500.00".
 * Retorna null quando não há número no texto.
 *
 * Regras (`milhar: true`, campos de valor):
 *  - "." e "," juntos: o ÚLTIMO dos dois é o decimal ("1.500,00" e "1,500.00" → 1500.00);
 *  - só ",": decimal ("1500,5" → 1500.5). Várias vírgulas em grupos de 3
 *    ("1,500,000") são milhar no padrão americano;
 *  - só ".": milhar quando todo grupo depois dele tem 3 dígitos ("1.500" → 1500,
 *    "1.500.000" → 1500000); senão decimal ("1500.5", "12.75");
 *  - símbolos de moeda, espaços, "%" e letras são descartados.
 *
 * `milhar: false` (percentual, câmbio): não existe milhar — o último separador,
 * seja "." ou ",", é o decimal ("19.05" / "19,05" / "19,05%" → 19.05).
 *
 * Caso de propósito (decisão pt-BR): "1,500" — vírgula ÚNICA seguida de 3 dígitos —
 * é 1,5 (vírgula é o decimal brasileiro), não 1500. Só vírgulas em série de grupos
 * de 3 ("1,500,000") ou junto com ponto ("1,500.00") viram milhar americano.
 */
export const normalizarNumeroColado = (texto, { milhar = true } = {}) => {
    if (texto === null || texto === undefined) return null;
    const bruto = String(texto).trim();
    if (!/\d/.test(bruto)) return null;

    // Sinal: "-" / "−" (menos tipográfico) antes do número ou contábil "(1.500,00)".
    const soSinais = bruto.replace(/[^\d.,\-−()]/g, '');
    const negativo = /^[-−(]/.test(soSinais);

    const limpo = bruto.replace(/[^\d.,]/g, '');
    const ultimoPonto = limpo.lastIndexOf('.');
    const ultimaVirgula = limpo.lastIndexOf(',');

    let inteiro;
    let fracao = '';
    const cortarNoUltimo = (indice) => {
        inteiro = limpo.slice(0, indice).replace(/[.,]/g, '');
        fracao = limpo.slice(indice + 1).replace(/[.,]/g, '');
    };

    if (ultimoPonto < 0 && ultimaVirgula < 0) {
        inteiro = limpo;
    } else if (ultimoPonto >= 0 && ultimaVirgula >= 0) {
        cortarNoUltimo(Math.max(ultimoPonto, ultimaVirgula));
    } else {
        const separador = ultimoPonto >= 0 ? '.' : ',';
        const partes = limpo.split(separador);
        const soUmSeparador = partes.length === 2;
        const tratarComoMilhar = milhar && ehAgrupamentoMilhar(partes)
            // Vírgula única é decimal no padrão brasileiro ("1,500" = 1,5).
            && !(separador === ',' && soUmSeparador);
        if (tratarComoMilhar) {
            inteiro = partes.join('');
        } else {
            cortarNoUltimo(separador === '.' ? ultimoPonto : ultimaVirgula);
        }
    }

    inteiro = (inteiro || '').replace(/^0+(?=\d)/, '');
    if (inteiro === '' && fracao === '') return null;
    if (inteiro === '') inteiro = '0';

    const numero = fracao ? `${inteiro}.${fracao}` : inteiro;
    const ehZero = !/[1-9]/.test(numero);
    return negativo && !ehZero ? `-${numero}` : numero;
};

/**
 * Arredonda (meio para cima) uma string numérica canônica para `casas`
 * decimais, sem passar por float — "1.005" com 2 casas vira "1.01", não "1.00".
 * `casas` null/undefined = sem limite.
 */
export const arredondarCasas = (numero, casas) => {
    if (numero === null || numero === undefined || numero === '') return numero;
    if (casas === null || casas === undefined) return numero;
    const negativo = numero.startsWith('-');
    const semSinal = negativo ? numero.slice(1) : numero;
    const [inteiro, fracao = ''] = semSinal.split('.');
    if (fracao.length <= casas) return numero;

    const digitos = (inteiro + fracao.slice(0, casas)).split('').map(Number);
    if (Number(fracao[casas]) >= 5) {
        let i = digitos.length - 1;
        while (i >= 0) {
            if (digitos[i] < 9) { digitos[i] += 1; break; }
            digitos[i] = 0;
            i -= 1;
        }
        if (i < 0) digitos.unshift(1);
    }
    const texto = digitos.join('');
    const corte = texto.length - casas;
    const novoInteiro = texto.slice(0, corte).replace(/^0+(?=\d)/, '') || '0';
    const novaFracao = texto.slice(corte);
    const resultado = casas > 0 ? `${novoInteiro}.${novaFracao}` : novoInteiro;
    return negativo && /[1-9]/.test(resultado) ? `-${resultado}` : resultado;
};

/**
 * Lê o `value` que o pai guarda HOJE (qualquer um dos formatos em uso) e devolve
 * a string numérica canônica ("-1500.5") ou '' — mesma leitura do NumericFormat:
 *  - number → como está;
 *  - string sem vírgula e numérica ("1500.500000", "-714") → como está (ponto = decimal);
 *  - string pt-BR ("1500,5", "1.500,50", "R$ -1.500,50") → sem milhar, vírgula vira ponto.
 */
export const numeroDoValor = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    if (typeof valor === 'number') {
        if (!Number.isFinite(valor)) return '';
        return valor.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
    }
    const str = String(valor).trim();
    if (str === '') return '';
    if (!str.includes(',') && /^[-−]?\d*\.?\d*$/.test(str) && /\d/.test(str)) {
        const negativo = /^[-−]/.test(str);
        let [inteiro, fracao = ''] = str.replace(/^[-−]/, '').split('.');
        inteiro = inteiro.replace(/^0+(?=\d)/, '') || '0';
        const numero = fracao ? `${inteiro}.${fracao}` : inteiro;
        return negativo && /[1-9]/.test(numero) ? `-${numero}` : numero;
    }
    const soSinais = str.replace(/[^\d,\-−]/g, '');
    const negativo = /^[-−]/.test(soSinais);
    const limpo = str.replace(/[^\d,]/g, '');
    if (!/\d/.test(limpo)) return '';
    const i = limpo.indexOf(',');
    let inteiro = i < 0 ? limpo : limpo.slice(0, i);
    const fracao = i < 0 ? '' : limpo.slice(i + 1).replace(/,/g, '');
    inteiro = inteiro.replace(/^0+(?=\d)/, '') || '0';
    const numero = fracao ? `${inteiro}.${fracao}` : inteiro;
    return negativo && /[1-9]/.test(numero) ? `-${numero}` : numero;
};

// Número (float) de qualquer formato em uso; NaN quando vazio/inválido.
export const paraNumero = (valor) => {
    const numero = numeroDoValor(valor);
    return numero === '' ? NaN : parseFloat(numero);
};

/**
 * Formata a string canônica em pt-BR: "1500.5" → "R$ 1.500,50".
 * `casasMax` arredonda; `casasMin` completa com zeros; entre os dois, zeros à
 * direita somem ("1234.560000" com min 2/max 6 → "1.234,56").
 * Negativo: "R$ -1.500,00" (mesmo jeito dos totais da grade).
 */
export const formatarNumero = (numero, { milhar = true, casasMin = 0, casasMax = null, prefixo = '', sufixo = '' } = {}) => {
    if (numero === null || numero === undefined || numero === '') return '';
    const arredondado = arredondarCasas(String(numero), casasMax);
    const negativo = arredondado.startsWith('-');
    let [inteiro, fracao = ''] = (negativo ? arredondado.slice(1) : arredondado).split('.');
    const minimo = Math.min(casasMin || 0, casasMax === null || casasMax === undefined ? Infinity : casasMax);
    while (fracao.length > minimo && fracao.endsWith('0')) fracao = fracao.slice(0, -1);
    while (fracao.length < minimo) fracao += '0';
    if (milhar) inteiro = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const sinal = negativo && /[1-9]/.test(inteiro + fracao) ? '-' : '';
    return `${prefixo}${sinal}${inteiro}${fracao ? ',' + fracao : ''}${sufixo}`;
};
